"use client";

import React, { useState } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";
import GalleryModal, {
  MediaItem,
} from "@/components/Layouts/components/GalleryModal";

export interface ProgressMilestone {
  id: string;
  index: string;
  status: string;
  title: string;
  image: string;
  images?: string[];
  gallery?: MediaItem[];
  picture?: string;
  picture_2?: string;
  picture_3?: string;
  picture_4?: string;
  picture_5?: string;
  picture_6?: string;
  imageOverlayTag?: string;
  imageOverlayTitle?: string;
}

export interface ConstructionProgressData {
  sectionIndex?: string;
  totalSections?: string;
  tag?: string;
  title?: string;
  description?: string;
  ctaLink?: {
    label: string;
    url?: string;
  };
  manifesto?: {
    tag: string;
    title: string;
    description: string;
    footer?: string;
  };
  milestones?: ProgressMilestone[];
}

interface ConstructionProgressSectionProps {
  data: ConstructionProgressData;
  onCtaClick?: () => void;
  onMilestoneSelect?: (milestoneId: string) => void;
}

export default function ConstructionProgressSection({
  data,
  onCtaClick,
  onMilestoneSelect,
}: ConstructionProgressSectionProps) {
  if (!data || !data.milestones || data.milestones.length === 0) return null;

  const { tag = "", title = "", description = "", milestones = [] } = data;

  const [activeMilestoneId, setActiveMilestoneId] = useState<string>(
    milestones[0]?.id || "ha-tang-nen-mong",
  );

  // Gallery Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMedia, setModalMedia] = useState<MediaItem[]>([]);
  const [modalIndex, setModalIndex] = useState(0);

  const activeMilestone =
    milestones.find((m) => m.id === activeMilestoneId) || milestones[0];

  const handleSelectMilestone = (m: ProgressMilestone) => {
    setActiveMilestoneId(m.id);
    if (onMilestoneSelect) {
      onMilestoneSelect(m.id);
    }
  };

  const handleImageClick = (milestone: ProgressMilestone) => {
    let milestoneMedia: MediaItem[] = [];

    if (milestone.gallery && milestone.gallery.length > 0) {
      milestoneMedia = milestone.gallery;
    } else if (milestone.images && milestone.images.length > 0) {
      milestoneMedia = milestone.images.map((img) => ({
        type: "image",
        src: img,
        title: milestone.title || milestone.imageOverlayTitle || "",
      }));
    } else {
      const pics = [
        milestone.picture,
        milestone.picture_2,
        milestone.picture_3,
        milestone.picture_4,
        milestone.picture_5,
        milestone.picture_6,
      ].filter((p): p is string =>
        Boolean(p && typeof p === "string" && p.trim() !== ""),
      );

      const allPics =
        pics.length > 0 ? pics : milestone.image ? [milestone.image] : [];
      const uniquePics = Array.from(new Set(allPics));

      const titleText = milestone.title || milestone.imageOverlayTitle || "";

      milestoneMedia = uniquePics.map((src) => ({
        type: "image",
        src,
        title: titleText,
      }));
    }

    if (milestoneMedia.length > 0) {
      setModalMedia(milestoneMedia);
      setModalIndex(0);
      setModalOpen(true);
    }
  };

  return (
    <section
      id="tien-do-du-an"
      className="relative w-full h-auto lg:h-screen lg:min-h-screen lg:max-h-screen flex flex-col lg:flex-row items-stretch overflow-hidden bg-[#0A1B24] text-white select-none"
    >
      {/* ================= LEFT COLUMN: MAIN PROGRESS IMAGE (~52% WIDTH) ================= */}
      <div
        onClick={() => handleImageClick(activeMilestone)}
        className="relative w-full lg:w-[52%] min-h-[300px] sm:min-h-[380px] md:min-h-[440px] lg:h-full overflow-hidden group flex-shrink-0 cursor-pointer"
      >
        <SmoothImage
          key={activeMilestone.id}
          src={activeMilestone.image}
          alt={activeMilestone.title}
          fill
          sizes="(max-width: 1024px) 100vw, 52vw"
          priority
          className="object-cover object-center transition-all duration-700 group-hover:scale-105"
        />

        {/* Dark Gradient Overlay */}
        <div className="hidden lg:block absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none z-10" />

        {/* Image Bottom Left Overlay Content (WITH INCREASED INSET MARGINS) */}
        <div className="absolute inset-x-0 bottom-0 p-8 sm:p-12 md:p-14 lg:p-16 z-20 flex flex-col justify-end text-white pointer-events-none">
          <span className="text-[10px] sm:text-xs font-sans font-bold tracking-[0.2em] text-white/75 uppercase mb-1.5">
            {activeMilestone.imageOverlayTag || "CẬP NHẬT GẦN NHẤT"}
          </span>
          <h3 className="font-lora text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal text-white leading-tight">
            {renderTextWithBreaks(
              activeMilestone.imageOverlayTitle || "Hành trình\nđang tiếp diễn",
            )}
          </h3>
        </div>
      </div>

      {/* ================= RIGHT COLUMN: MILESTONE LIST WITH LINEAR GRADIENT & DECO EFFECT (~48% WIDTH) ================= */}
      <div className="w-full lg:w-[48%] flex-1 lg:h-full relative overflow-hidden flex flex-col justify-between z-20">
        {/* BACKGROUND GRADIENT IN TAILWIND PRIMARY & PRIMARY DARK PALETTE */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#085C47] to-primaryDark pointer-events-none z-0 opacity-95" />
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/20 rounded-full blur-3xl pointer-events-none z-0" />

        {/* DECORATIVE SHADOW OVERLAY IMAGE */}
        <div className="absolute inset-y-0 right-0 w-full h-full pointer-events-none z-[1] opacity-90 mix-blend-overlay">
          <Image
            src="/images/deco_shadow_left_bottom.png"
            alt="Decorative Shadow Overlay"
            fill
            sizes="(max-width: 1024px) 100vw, 48vw"
            priority
            className="object-cover object-right-bottom"
          />
        </div>

        <div className="relative z-10 w-full h-full px-6 sm:px-10 lg:px-14 xl:px-16 pt-8 sm:pt-12 lg:pt-24 pb-8 sm:pb-10 flex flex-col justify-between overflow-y-auto">
          <div className="space-y-6">
            {/* TOP HEADER ROW */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-[1px] bg-[#C9AB81]" />
                  <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#C9AB81] font-sans">
                    {tag}
                  </span>
                </div>
              </div>

              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-white tracking-tight pt-1 title-leading"
                style={{ lineHeight: 1.15 }}
              >
                {renderTextWithBreaks(title)}
              </h2>

              <p className="text-xs font-sans font-light text-white/70 leading-relaxed max-w-md pt-1">
                {renderTextWithBreaks(description)}
              </p>
            </div>

            {/* INTERACTIVE MILESTONES LIST */}
            <div className="w-full border-t border-white/15 pt-1 space-y-1">
              {milestones.map((m) => {
                const isSelected = activeMilestoneId === m.id;

                return (
                  <div
                    key={m.id}
                    onClick={() => handleSelectMilestone(m)}
                    className={cn(
                      "w-full flex items-center justify-between py-3.5 sm:py-4.5 transition-all duration-300 cursor-pointer group select-none",
                      isSelected
                        ? "border-b-2 border-[#C9AB81]"
                        : "border-b border-white/10 hover:border-white/30",
                    )}
                  >
                    {/* LEFT: INDEX & STATUS */}
                    <div className="flex items-center gap-4 sm:gap-6">
                      <span
                        className={cn(
                          "font-lora text-base sm:text-lg font-normal transition-colors",
                          isSelected ? "text-[#C9AB81]" : "text-white/40",
                        )}
                      >
                        {m.index}
                      </span>

                      <span
                        className={cn(
                          "text-[9px] sm:text-[10px] font-sans uppercase tracking-[0.18em] transition-colors",
                          isSelected
                            ? "text-[#C9AB81] font-bold"
                            : "text-white/40 group-hover:text-white/60 font-medium",
                        )}
                      >
                        {m.status}
                      </span>
                    </div>

                    {/* RIGHT: MILESTONE TITLE */}
                    <div
                      className={cn(
                        "font-lora text-sm sm:text-base lg:text-lg font-normal text-right transition-colors",
                        isSelected
                          ? "text-[#C9AB81] font-medium"
                          : "text-white/60 group-hover:text-white",
                      )}
                    >
                      {m.title}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* REUSABLE GALLERY MODAL */}
      <GalleryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        media={modalMedia}
        initialIndex={modalIndex}
      />
    </section>
  );
}
